import React from 'react';

/*
 *
 * 1-1 project negotiation actions
 *
 */
const SEND_OFFER = {
  label: 'Send Offer',
  nextStatus: 'client_sent_offer',
  allowsComment: true,
  requiresNewValue: true,
  requiresNewCreative: true,
  doneTitle: 'You sent an offer',
  doneDetails: <span>
    We'll notify the creative and we'll let you know<br className="hide-for-small-only"/> when
    they respond to your offer.
  </span>
};
const REQUEST_QUOTE = {
  label: 'Request Quote',
  nextStatus: 'client_requested_quote',
  allowsComment: true,
  requiresNewCreative: true,
  doneTitle: 'You requested a quote',
  doneDetails: <span>
    We'll notify the creative and we'll let you know<br className="hide-for-small-only"/> when
    they respond to your request.
  </span>
};
const SEND_QUOTE = (project) => ({
  label: 'Provide Quote',
  nextStatus: 'creative_sent_quote',
  requiresNewValue: true,
  allowsComment: true,
  disclaimer: `I understand that if the client accepts my quote, I will participate in the ${project.mini? '' : '1-to-1'} project as described by the client and turn over the final intellectual property for my work when the project is complete.`,
  doneTitle: 'You sent a quote',
  doneDetails: <span>
    We'll notify the client and we'll let you know<br className="hide-for-small-only"/> when
    they respond to your quote.
  </span>
});
const CREATIVE_DECLINE = (project) => ({
  label: 'Decline Offer',
  confirmation: `Are you sure you want to permanently decline the client's offer to work ${project.mini? 'in this project' : '1-to-1'}?`,
  nextStatus: 'creative_declined',
  allowsComment: true,
  showValue: true,
  isNegative: true,
  doneTitle: 'You declined to participate',
  doneDetails: "We'll notify the client."
});
const CREATIVE_ACCEPT = (project) => ({
  label: 'Accept Offer',
  confirmation: `Are you sure you want to accept the client's offer to work ${project.mini? 'in this project' : '1-to-1'}?`,
  nextStatus: 'creative_accepted',
  showValue: true,
  disclaimer: `I will participate in this ${project.mini? '' : '1-to-1'} project as described by the client and turn over the final intellectual property for my work when the project is complete.`,
  doneTitle: 'You accepted to participate',
  doneDetails: "We'll notify the client and ask them to provide payment. Once they do, we will let you know and you will be able to start work for this project."
});
const SEND_COUNTER_QUOTE = (project) => ({
  label: 'Send Counter-Offer',
  nextStatus: 'creative_sent_counter_quote',
  requiresNewValue: true,
  allowsComment: true,
  disclaimer: `I understand that if the client accepts my counter-offer, I will participate in the ${project.mini? '' : '1-to-1'} project as described by the client and turn over the final intellectual property for my work when the project is complete.`,
  doneTitle: 'You sent a counter-offer',
  doneDetails: <span>
    We'll notify the client and we'll let you know<br className="hide-for-small-only"/> when
    they respond to your counter-offer.
  </span>
});
const CLIENT_ACCEPT = {
  label: 'Accept Quote',
  confirmation: "Are you sure you want to accept the creative's quote?",
  nextStatus: 'client_accepted',
  showValue: true,
  disclaimer: 'I understand that by accepting this quote, I will work directly with the creative as I have described.',
  doneTitle: "You accepted creative's quote",
  doneDetails: "We'll notify the creative."
};
const SEND_COUNTER_OFFER = {
  label: 'Send Counter-Offer',
  nextStatus: 'client_sent_counter_offer',
  allowsComment: true,
  requiresNewValue: true,
  doneTitle: 'You sent a counter-offer',
  doneDetails: <span>
    We'll notify the creative and we'll let you know<br className="hide-for-small-only"/> when
    they respond to your counter-offer.
  </span>
};
const CLIENT_DECLINE = {
  label: 'Decline',
  confirmation: "Are you sure you want to permanently decline the creative's quote?",
  nextStatus: 'client_declined',
  allowsComment: true,
  showValue: true,
  isNegative: true,
  doneTitle: "You declined creative's quote",
  doneDetails: "We'll notify the creative."
};
const CLIENT_PAY = {
  label: 'Pay Now'
};

/*
 *
 * 1-1 project negotiation statuses and related definitions
 *
 */
const NEGOTIATION_STATUSES = (project) => ({
  not_started: {
    label: '-',
    showSuggestedCreatives: true,
    actor: 'client',
    actions: [SEND_OFFER, REQUEST_QUOTE]
  },
  client_requested_quote: {
    label: 'Quote Requested',
    actor: 'creative',
    actions: [SEND_QUOTE(project), CREATIVE_DECLINE(project)]
  },
  client_sent_offer: {
    label: (isClient, isCreative, isCollab) => (isClient || isCollab ? 'Offer Sent' : 'Offer Received'),
    actor: 'creative',
    actions: [CREATIVE_ACCEPT(project), SEND_COUNTER_QUOTE(project), CREATIVE_DECLINE(project)]
  },
  client_sent_counter_offer: {
    label: (isClient, isCreative, isCollab) => (isClient || isCollab ? 'You Countered' : 'Client Countered'),
    actor: 'creative',
    actions: [CREATIVE_ACCEPT(project), SEND_COUNTER_QUOTE(project), CREATIVE_DECLINE(project)]
  },
  client_declined: {
    label: 'Declined',
    actor: 'client',
    showSuggestedCreatives: true,
    actions: [SEND_OFFER, REQUEST_QUOTE]
  },
  client_accepted: {
    label: (isClient, isCreative, isCollab) => (isClient || isCollab ? 'Awaiting Payment' : 'Client Accepted'),
    actor: 'client',
    actions: [CLIENT_PAY],
  },
  creative_sent_quote: {
    label: (isClient, isCreative, isCollab) => (
      (isClient || isCollab) ? 'Quote Received' : 'Quote Sent'
    ),
    actor: 'client',
    actions: [CLIENT_ACCEPT, SEND_COUNTER_OFFER, CLIENT_DECLINE]
  },
  creative_sent_counter_quote: {
    label: (isClient, isCreative, isCollab) => (isClient || isCollab ? 'Creative Countered' : 'You Countered'),
    actor: 'client',
    actions: [CLIENT_ACCEPT, SEND_COUNTER_OFFER, CLIENT_DECLINE]
  },
  creative_declined: {
    label: 'Declined',
    actor: 'client',
    showSuggestedCreatives: true,
    actions: [SEND_OFFER, REQUEST_QUOTE]
  },
  creative_accepted: {
    label: (isClient, isCreative, isCollab) => (isClient || isCollab ? 'Awaiting Payment' : 'Creative Accepted'),
    actor: 'client',
    actions: [CLIENT_PAY],
  }
});

export default NEGOTIATION_STATUSES;



// WEBPACK FOOTER //
// ./project/statuses_1_1.jsx